import { ColumnDef } from "@tanstack/react-table";
import Table, { buildActionColumn } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import { toFormat } from "@/lib/dateHelpers";
import type { UpcomingMemberElectionDateNode } from "../types";

function buildColumns(
  setMenu: (menu: TableRowMenu) => void,
  onEdit: (row: UpcomingMemberElectionDateNode) => void,
  onDelete: (row: UpcomingMemberElectionDateNode) => void
): ColumnDef<UpcomingMemberElectionDateNode, unknown>[] {
  return [
    {
      id: "electionDate",
      header: "Election Date",
      accessorFn: (row) => row.electionDate,
      cell: ({ row }) => toFormat(row.original.electionDate, "MMM dd, yyyy") ?? "--",
      size: 260,
      enableSorting: true
    },
    {
      id: "dayOfWeek",
      header: "Day of the Week",
      cell: ({ row }) => toFormat(row.original.electionDate, "EEEE") ?? "--",
      size: 260
    },
    buildActionColumn<UpcomingMemberElectionDateNode>({
      getActions: (row) => [
        { text: "Edit Date", iconName: "edit", onClick: () => onEdit(row) },
        { text: "Delete Date", iconName: "delete", destructive: true, onClick: () => onDelete(row) }
      ],
      actionId,
      setMenu,
      getRowId: (row) => row.id
    })
  ];
}

export type Props = {
  loading: boolean;
  dates: UpcomingMemberElectionDateNode[];
  setMenu: (menu: TableRowMenu) => void;
  onEdit: (row: UpcomingMemberElectionDateNode) => void;
  onDelete: (row: UpcomingMemberElectionDateNode) => void;
  onAdd: () => void;
};

export default function UpcomingDatesTable({ loading, dates, setMenu, onEdit, onDelete, onAdd }: Props) {
  const sortedDates = [...dates].sort((a, b) => a.electionDate.localeCompare(b.electionDate));

  return (
    <Table
      loading={loading}
      data={sortedDates}
      columns={buildColumns(setMenu, onEdit, onDelete)}
      data-testid="upcoming-dates-table"
      renderNoDataEmptyState={() => (
        <EmptyState
          iconName="how_to_vote"
          title="No Upcoming Member Election Dates"
          caption="There are no upcoming member election dates, which prevents automatic election of eligible people."
          action={{ onClick: onAdd, icon: "add", children: "Add Election Date" }}
        />
      )}
    />
  );
}
